// import { DataSource } from 'ng2-smart-table/lib/lib/data-source/data-source';
import { Observable } from 'rxjs';

import { ServerDataSource } from 'angular2-smart-table';
// import { ViewCell } from 'ng2-smart-table';
import { Model } from './common/.base.model';
import { Department } from './department';
import { Application } from './application';
import { size } from 'lodash';
import { cv } from '@fullcalendar/core/internal-common';
import { EntityConfigTemplate } from './common/entity-config.interface';
import { FormConfigTemplate } from './common/form-config-template.interface';
import { User } from '../data/users';

export interface Person extends Model {
  // Backend fields (exist in Strapi schema)
  id?: number;
  documentId?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: string;
  email?: string;
  phone?: string;
  address?: string;
  socialSecurityNumber?: string;
  dob?: Date | string;
  assignments?: string[];
  department?: Department;
  departmentManaging?: string;
  leaveRequests?: string[];
  performances?: string[];
  appraisals?: string[];
  okrs?: string[];
  applications?: Application[];
  trainings?: string[];
  CV?: any;
  desiredPosition?: string;
  defaultVacationDaysAmount?: number;
  joinedOn?: Date | string;
  user: User | any;

}

export interface Assignment {
  id?: number;
  documentId?: string;
  role?: JobRole;
  startDate?: string;
  endDate?: string;
  person?: Person;
}

export interface JobRole {
  id: number;
  title: string;
  description?: string;
}

export interface LeaveRequest {
  id: number;
  type: string;
  startDate: string;
  endDate: string;
  reason?: string;
  person?: Person;
  leaveStatus: string;
  requestedAt: string;
  reviewedBy?: Person;
  reviewedAt?: string;
}

export interface Performance {
  id: number;
  metric: string;
  score: number;
  periodStart: string;
  periodEnd: string;
  notes?: string;
}

// Active/Inactive/Suspended/Left are the statuses payroll accepts (case-sensitive); applicants are stored as
// lowercase 'applicant', which the rest of the app matches on (applicant users page, department manager filter)
const statusOptions = [
  { value: 'Active', label: 'Active'},
  { value: 'Inactive', label: 'Inactive'},
  { value: 'Suspended', label: 'Suspended'},
  { value: 'Left', label: 'Left'},
  { value: 'applicant', label: 'Applicant'},
  // { value: 'Temporary', label: 'temporary'},
];

const genderOptions = [
  { value: 'Male', label: 'male' },
  { value: 'Female', label: 'female' },
  { value: 'Other', label: 'other' },
  { value: 'Prefer not to say', label: 'prefer not to say' },
];

export const PersonConfig: EntityConfigTemplate = {
  meta: { entityName: "Personnel", entityPluralName: "Personnel", titleField: 'name', searchFields: ['firstName', 'lastName'], view: false}, //view:false is used to hide this attr from views
  documentId: { view: false },
  // Payroll employee number, assigned once the person is synced; blank until then
  erpnextEmployeeId: { title: "Employee ID", valuePrep: (_cell_, row) => row?.erpnextEmployeeId || '—' },
  name: { title: "Name", valuePrep: (_cell_, row) => `${row?.firstName} ${row?.lastName}`, calculatedField: true, view: { list: false } },
  firstName: { type: "text" },
  middleName: { type: "text", view: { list: false }},
  lastName: { type: "text" },
  currentAssignment: { title: "Role", valuePrep: (_cell_, row) => row?.assignments ? row.assignments?.[0]?.role?.title : '' },
  email: { type: "text" },
  employmentStatus: {default: "Inactive"},
  phone: { view: { list: false }},
  dob: { view: { list: false }},
  gender: {default: "Prefer not to say" },
  address: { view: { list: false }},
  socialSecurityNumber: { view: { list: false }},
  department: { title: "Team", isForeignKey: true,  view: { list: false }},
  assignments: { isForeignKey: true,  view: false},
  leaveRequests: { isForeignKey: true,  view: false},
  performances: { isForeignKey: true,  view: { list: false }},
  appraisals: { isForeignKey: true,  view: { list: false }},
  okrs: { isForeignKey: true,  view: { list: false }},
  applications: { isForeignKey: true,  view: { list: false }},
  trainings: { isForeignKey: true,  view: { list: false }},
  CV: { title: "CV", type: "file", view: { list: false }},
  defaultVacationDaysAmount: { title: "Starting Vacation Days", type: "number", view: { list: false }},
  user: { title: "User Account", isForeignKey: true, view: { list: false }, filters: { role: { name: { $ne: "Applicant" } } } },
  createdAt: {view: { list: false }},
  updatedAt: {valuePrep: (_cell_, row) => row?.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '' },
}

export const PersonFormConfig: FormConfigTemplate = {
  tabs: [
    {
      title: "Person Details",
      columns: [
        {
          size: "col-12 col-lg-4 col-xl-3",
          type: "profile",
        },
        {
          type: "form",
          size: "col-12 col-lg-8 col-xl-9",
          sections: [
            {
              title: "Personnel Information",
              rows: [
                {
                  fields: [
                    { name: "firstName", label: "First Name", type: "text", required: true, size: "col-4" },
                    { name: "middleName", label: "Middle Name", type: "text", size: "col-4" },
                    { name: "lastName", label: "Last Name", type: "text", size: "col-4" },
                  ]
                },
                {
                  fields: [
                    // { name: "email", label: "Email", type: "text", size:"col-6" },
                    { name: "dob", label: "DOB", type: "date", size: "col-6" },
                    { name: "gender", label: "Gender", type: "select", options: genderOptions, size: "col-6" },
                  ]
                },
                {
                  fields: [
                    { name: "phone", label: "Phone", type: "text", size:"col-6" },
                    { name: "email", label: "Email", type: "text", size:"col-6" },
                    { name: "socialSecurityNumber", label: "Social Security", type: "text", size: "col-12" },
                  ]
                },
                {
                  fields: [
                    { name: "address", label: "Address", type: "text", size:"col-12" },
                  ]
                },
                {
                  fields: [
                    { name: "CV", label: "CV", type: "file", size:"col-12" },
                  ]
                }
              ]
            },
            {
              title: "Employment Information",
              rows: [
                {
                  fields: [
                    { name: "department", label: "Team", type: "selectRelation", entityName: "department", size: "col-4" },
                    { name: "user", label: "User Account", type: "selectRelation", entityName: "user", size: "col-4" },
                    { name: "employmentStatus", label: "Employment Status", type: "select", options: statusOptions, size: "col-4" },
                  ],
                },
                {
                  fields: [
                    { name: "defaultVacationDaysAmount", label: "Starting Vacation Days", type: "number", size: "col-6" },
                    { name: "joinedOn", label: "Joined On", type: "date", size: "col-6" },
                  ]
                }
              ]
            },
          ]
        }
      ]
    },
    {
      title: "Assignments",
      columns: [
        {
          type: "tableRelation",
          size: "col-12",
          name: "assignments",
          entityName: "assignment",
        }
      ]
    },
    {
      title: "Leave Requests",
      columns: [
        {
          type: "tableRelation",
          size: "col-12",
          name: "leaveRequests",
          entityName: "leave-request",
          sections: [
            {
              type: "form",
              rows: [
                {
                  fields: [
                    { name: "defaultVacationDaysAmount", label: "Starting Vacation Days", type: "number", disabled: true, size: "col-4" },
                    { name: "remainingVacationDays", label: "Remaining Vacation Days", type: "number", disabled: true, size: "col-4" },
                    { name: "sickDaysConsumed", label: "Sick Days Used", type: "number", disabled: true, size: "col-4" },
                  ]
                }
              ]
            }
          ]
        }
      ]
    }
  ]
}

export function getPersonCalculatedFields(): string[] {
  let arr: string[] = [];
  Object.entries(PersonConfig).forEach(([key, config]: any) => {
    if (config?.calculatedField === true) {
      arr.push(key);
    }
  });
  return arr;
}


export abstract class PersonData {
  abstract get gridDataSource(): ServerDataSource;
  abstract getCustomGridDataSource(customFilter?: string): ServerDataSource;
  abstract listAll(filters?: { [field: string]: any }): Observable<Person[]>;
  abstract list(pageNumber: number, pageSize: number): Observable<Person[]>;
  abstract get(id: number): Observable<Person>;
  abstract update(Book: Person): Observable<Person>;
  abstract create(Person: Person): Observable<Person>;
  abstract delete(id: string): Observable<boolean>;
}

